import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { LoadingState } from '../../components/ui/Spinner';
import { formatAmount, formatDate } from '../../lib/format';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Map i18n language codes to locale codes for date formatting
const localeMap: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  ru: 'ru-RU',
};

// Format date using the current locale (e.g., "28 January 2026" or "28 января 2026")
function formatDateLocale(date: Date, language: string): string {
  const locale = localeMap[language] || 'en-US';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation(['common', 'transactions']);
  const { currentEntity } = useEntity();
  const { user } = useAuth();

  const { data: balances, isLoading: balancesLoading } = useQuery({
    queryKey: ['balances', currentEntity?.id],
    queryFn: () => api.reports.balances(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const { data: debt, isLoading: debtLoading } = useQuery({
    queryKey: ['debt', currentEntity?.id],
    queryFn: () => api.debt.list(currentEntity!.id, { limit: 5 }),
    enabled: !!currentEntity,
  });

  const { data: recentTx, isLoading: txLoading } = useQuery({
    queryKey: ['transactions', currentEntity?.id, { limit: 5 }],
    queryFn: () => api.transactions.list(currentEntity!.id, { limit: 5 }),
    enabled: !!currentEntity,
  });

  if (!currentEntity) return null;

  // Get current date in the user's locale
  const currentDate = formatDateLocale(new Date(), i18n.language);

  // Group balances by currency type for multi-column display


  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('nav.dashboard')}</h1>
          <p className="page-subtitle">{user?.fullName}</p>
        </div>
        <div className="current-date">
          {currentDate}
        </div>
      </div>

      <div className="quick-toolbar">
        <Link to="/transactions/new?type=exchange" className="toolbar-btn">
          <span>💱</span> {t('transactions:quickActions.exchange')}
        </Link>
        <Link to="/transactions/new?type=expense" className="toolbar-btn">
          <span>📤</span> {t('transactions:quickActions.expense')}
        </Link>
        <Link to="/transactions/new?type=deposit" className="toolbar-btn">
          <span>📥</span> {t('transactions:quickActions.deposit')}
        </Link>
        <Link to="/transactions/new?type=withdrawal" className="toolbar-btn">
          <span>💸</span> {t('transactions:quickActions.withdrawal')}
        </Link>
        <Link to="/transactions/new?type=transfer" className="toolbar-btn">
          <span>🔄</span> {t('transactions:quickActions.transfer')}
        </Link>
        <div className="toolbar-divider" />
        <Link to="/reports" className="toolbar-btn">
          <span>📊</span> {t('transactions:quickActions.reports')}
        </Link>
        <Link to="/debt" className="toolbar-btn">
          <span>📋</span> {t('transactions:quickActions.debt')}
        </Link>
      </div>

      {/* Wallet Balances - Multi-column layout */}
      <div className="wallet-balances-section">
        <div className="section-header">
          <h2 className="section-title">{t('common:dashboard.walletBalances')}</h2>
          <Link to="/admin/wallets" className="view-all-link">View all →</Link>
        </div>
        {balancesLoading ? (
          <LoadingState message="Loading balances..." />
        ) : (
          <div className="balances-grid">
            {balances?.totals && balances.totals.length > 0 ? (
              balances.totals.map((item) => (
                <div key={item.currency} className="balance-card">
                  <div className="balance-card-header">
                    <span className="currency-icon">
                      {['USDT', 'BTC', 'ETH', 'USDC', 'BNB'].includes(item.currency.toUpperCase()) ? '🪙' : '💵'}
                    </span>
                    <span className="currency-code">{item.currency}</span>
                  </div>
                  <div className={`balance-value ${Number(item.amount) < 0 ? 'balance-negative' : ''}`}>{formatAmount(item.amount)}</div>
                </div>
              ))
            ) : (
              <p className="text-muted">No wallets configured</p>
            )}
          </div>
        )}
      </div>

      <div className="dashboard-grid">

        {/* Open Debts */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Open Debts</h2>
            <Link to="/debt" className="text-muted">View all</Link>
          </div>
          {debtLoading ? (
            <LoadingState message="Loading debts..." />
          ) : (
            <div>
              {debt?.totals && debt.totals.length > 0 ? (
                <>
                  <div className="debt-totals">
                    {debt.totals.map((t) => (
                      <div key={t.currency} className="debt-total">
                        <span className="debt-currency">{t.currency}</span>
                        <span className="debt-amount">{formatAmount(t.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-muted mt-4">
                    {debt.meta.total} open transaction{debt.meta.total !== 1 ? 's' : ''} with debt
                  </p>
                </>
              ) : (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  <span>No outstanding debts</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="card card-wide">
          <div className="card-header">
            <h2 className="card-title">{t('common:dashboard.recentTransactions')}</h2>
            <Link to="/transactions" className="text-muted">View all</Link>
          </div>
          {txLoading ? (
            <LoadingState message="Loading transactions..." />
          ) : recentTx?.data && recentTx.data.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTx.data.map((tx) => (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.transactionDate || tx.createdAt)}</td>
                    <td><span className={`badge badge-${tx.type}`}>{capitalize(tx.type)}</span></td>
                    <td className="font-mono">
                      {tx.type === 'exchange'
                        ? `${formatAmount(tx.amountIn, tx.currencyIn)} ${tx.currencyIn} → ${formatAmount(tx.amountOut, tx.currencyOut)} ${tx.currencyOut}`
                        : `${formatAmount(tx.amount, tx.currency)} ${tx.currency}`}
                    </td>
                    <td><span className={`badge badge-${tx.status}`}>{capitalize(tx.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted text-center">{t('common:messages.noTransactions')}</p>
          )}
        </div>

      </div>

      <style>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        .page-subtitle {
          color: var(--color-gray-500);
          margin-top: 4px;
        }
        .current-date {
          font-size: 16px;
          font-weight: 600;
          color: var(--color-text);
          padding: 8px 16px;
          background: var(--color-gray-100);
          border-radius: 8px;
        }

        /* Wallet Balances Section */
        .wallet-balances-section {
          margin-bottom: 24px;
        }
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .section-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }
        .view-all-link {
          font-size: 14px;
          color: var(--color-primary);
          text-decoration: none;
        }
        .view-all-link:hover {
          text-decoration: underline;
        }
        .balances-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }
        .balance-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 12px;
          padding: 20px;
          transition: box-shadow 0.15s, border-color 0.15s;
          overflow: hidden;
        }
        .balance-card:hover {
          border-color: var(--color-primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        .balance-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }
        .currency-icon {
          font-size: 20px;
        }
        .currency-code {
          font-size: 14px;
          font-weight: 600;
          color: var(--color-text-muted);
        }
        .balance-value {
          font-size: 20px;
          font-weight: 700;
          font-family: 'SF Mono', monospace;
          color: var(--color-success);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .balance-value.balance-negative {
          color: var(--color-error);
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }
        .card-wide {
          grid-column: 1 / -1;
        }
        .debt-totals {
          display: flex;
          gap: 24px;
        }
        .debt-total {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .debt-currency {
          font-size: 12px;
          color: var(--color-gray-500);
        }
        .debt-amount {
          font-size: 24px;
          font-weight: 600;
          font-family: monospace;
          color: var(--color-error);
        }
        .success-message {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #065f46;
          padding: 16px;
          background: #d1fae5;
          border-radius: var(--radius);
        }
        .success-icon {
          font-size: 20px;
        }
        .quick-toolbar {
          display: flex;
          gap: 8px;
          padding: 10px 12px;
          background: var(--color-gray-50);
          border: 1px solid var(--color-gray-200);
          border-radius: 6px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .toolbar-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--color-bg);
          border: 1px solid var(--color-gray-300);
          border-radius: 4px;
          text-decoration: none;
          color: var(--color-text);
          font-size: 13px;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .toolbar-btn:hover {
          background: var(--color-gray-100);
          border-color: var(--color-gray-400);
          text-decoration: none;
        }
        .toolbar-divider {
          width: 1px;
          background: var(--color-gray-200);
          margin: 0 4px;
        }
        .font-mono {
          font-family: monospace;
        }
        .badge-exchange { background: #dbeafe; color: #1e40af; }
        .badge-expense { background: #fee2e2; color: #991b1b; }
        .badge-deposit { background: #d1fae5; color: #065f46; }
        .badge-withdrawal { background: #fef3c7; color: #92400e; }
        .badge-transfer { background: #e0e7ff; color: #3730a3; }
      `}</style>
    </div>
  );
}
